/**
 * The registered deals behind the range.
 *
 * Showing the working is what turns a number into evidence. A broker arguing
 * price with an owner does not need a better average — they need eight real
 * deeds with dates they can point at. Everything here is a registered sale from
 * the Ministry of Justice open data, never a listing on this site, and the
 * subtitle says so because the difference is the entire point.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatNumber, formatPrice } from '../../utils/propertyUtils';
import { latnLocale } from '../../utils/numberFormat';
import type { MarketComp } from '../../types/marketData';

export interface ComparableDealsProps {
  comps: MarketComp[];
  /** Collapsed height; the rest appear behind "Show all deals". */
  initial?: number;
  className?: string;
}

export default function ComparableDeals({ comps, initial = 5, className = '' }: ComparableDealsProps) {
  const { t, i18n } = useTranslation('marketData');
  const [expanded, setExpanded] = useState(false);

  if (!comps?.length) return null;

  const shown = expanded ? comps : comps.slice(0, initial);
  const locale = i18n.language === 'ar' ? 'ar-SA' : 'en-GB';
  const fmtDate = (iso: string) =>
    new Intl.DateTimeFormat(latnLocale(locale), { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(iso));

  return (
    // A block inside the property page's «السوق في هذا الحي» section (no box of its own).
    <section className={className}>
      <header className="mb-4">
        <h3 className="text-base font-semibold text-[var(--pv-color-text)]">
          {t('comps.title', { defaultValue: 'Nearby registered deals' })}
        </h3>
        <p className="mt-1 text-[13px] text-[var(--pv-color-text-muted)]">
          {t('comps.subtitle', { defaultValue: 'Real deeds registered in this district - not listings' })}
        </p>
      </header>

      {/* A table, because this is tabular evidence and a broker will scan the
          per-m² column down the page. Cards would break that reading. */}
      <div className="-mx-1 overflow-x-auto">
        <table className="w-full min-w-[420px] border-collapse text-[13px]">
          <thead>
            <tr className="text-start text-[11px] font-semibold uppercase-none text-[var(--pv-color-text-subtle)]">
              <th scope="col" className="py-2 pe-3 text-start font-semibold">
                {t('comps.date', { defaultValue: 'Date' })}
              </th>
              <th scope="col" className="py-2 pe-3 text-start font-semibold">
                {t('comps.area', { defaultValue: 'Area' })}
              </th>
              <th scope="col" className="py-2 pe-3 text-start font-semibold">
                {t('comps.price', { defaultValue: 'Price' })}
              </th>
              <th scope="col" className="py-2 text-start font-semibold">
                {t('comps.perM2', { defaultValue: 'Per m²' })}
              </th>
            </tr>
          </thead>
          <tbody>
            {shown.map((c, i) => (
              <tr
                key={`${c.date}-${c.price}-${i}`}
                className="border-t border-[var(--pv-border-subtle)]"
              >
                <td className="py-2.5 pe-3 whitespace-nowrap text-[var(--pv-color-text-muted)]">
                  {fmtDate(c.date)}
                </td>
                <td className="py-2.5 pe-3 whitespace-nowrap tabular-nums text-[var(--pv-color-text-muted)]">
                  {formatNumber(c.area)} {t('comps.areaUnit', { defaultValue: 'm²' })}
                </td>
                <td className="py-2.5 pe-3 whitespace-nowrap tabular-nums font-medium text-[var(--pv-color-text)]">
                  {formatPrice(c.price)}
                </td>
                <td className="py-2.5 whitespace-nowrap tabular-nums font-semibold text-[var(--pv-color-primary)]">
                  {formatNumber(c.ppm)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {comps.length > initial && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-4 rounded-brand-sm px-1 text-[13px] font-semibold text-[var(--pv-color-primary)] underline-offset-4 hover:underline focus:outline-none focus-visible:shadow-brand-focus"
        >
          {expanded
            ? t('comps.showLess', { defaultValue: 'Show fewer' })
            : t('comps.showAll', { defaultValue: 'Show all deals' })}
        </button>
      )}
    </section>
  );
}
